import type {
  PlayerID,
  SourceContainerRef,
  VideoAction,
  VideoState,
} from '../types';

import { VideoJSPlayer } from 'video.js';
import {
  VisibilityStates,
  PlayState,
  VisibilityState,
  PlayStates,
} from '../../pop-out-media-manager/types';

export const playerExists = (state: VideoState, playerId: PlayerID): boolean =>
  !!state[playerId];

export const videoIsPopped = (
  currentState: VideoState,
  playerId: PlayerID,
): boolean => {
  return (
    playerExists(currentState, playerId) &&
    playerId === currentState.popOutState.id &&
    currentState.popOutState.visibilityState !== VisibilityStates.Hidden
  );
};

const updatePlayerSourceContainer = (
  state: VideoState,
  playerId: PlayerID,
  sourceContainer: SourceContainerRef,
): VideoState => {
  return {
    ...state,
    [playerId]: {
      ...state[playerId],
      sourceContainer: sourceContainer,
    },
  };
};

const addVideoPlayerAndSourceContainer = (
  state: VideoState,
  playerId: PlayerID,
  player: HTMLElement,
  sourceContainer: SourceContainerRef,
): VideoState => {
  return {
    ...state,
    [playerId]: {
      ...state[playerId],
      player,
      sourceContainer,
    },
  };
};

const removeVideoPlayerData = (
  state: VideoState,
  playerId: PlayerID,
): VideoState => {
  if (videoIsPopped(state, playerId)) {
    return state;
  }
  const newState = {
    ...state,
  };

  delete newState[playerId];
  if (newState.activeVideoState.id === playerId) {
    newState.activeVideoState = {
      ...state.activeVideoState,
      id: null,
      playState: PlayStates.NoPlayer,
    };
  }

  return newState;
};

const setVideoAsPopped = (
  state: VideoState,
  playerId: PlayerID,
): VideoState => {
  if (videoIsPopped(state, playerId)) {
    return state;
  }
  return {
    ...state,
    [playerId]: {
      ...state[playerId],
      sourceContainerVisibilityState: VisibilityStates.Hidden,
    },
    activeVideoState: {
      ...state.activeVideoState,
      id: playerId,
    },
    popOutState: {
      ...state.popOutState,
      visibilityState: VisibilityStates.Visible,
      id: playerId,
    },
  };
};

const removePoppedStateOfVideo = (
  state: VideoState,
  playerId: PlayerID,
): VideoState => {
  const newUnpoppedState = {
    ...state,
    [playerId]: {
      ...state[playerId],
      sourceContainerVisibilityState: VisibilityStates.Visible,
    },
    popOutState: {
      ...state.popOutState,
      visibilityState: VisibilityStates.Hidden,
      id: null,
    },
  };

  // if we couldn't pop it back to the original container, for example
  // because we changed page then remove the references we hold to the video
  const originalVideoContainerExistsStill =
    !!newUnpoppedState[playerId].sourceContainer.current;
  if (!originalVideoContainerExistsStill) {
    delete newUnpoppedState[playerId];
  }

  if (state.activeVideoState.id === playerId) {
    newUnpoppedState.activeVideoState = {
      ...state.activeVideoState,
      id: null,
      playState: PlayStates.NoPlayer,
    };
  }

  return newUnpoppedState;
};

const setVideoJSControllerInstance = (
  state: VideoState,
  playerId: PlayerID,
  controller: VideoJSPlayer,
): VideoState => {
  return {
    ...state,
    [playerId]: {
      ...state[playerId],
      controller: controller,
    },
  };
};

const setPopOutVisibility = (
  state: VideoState,
  visibilityState: VisibilityState,
): VideoState => {
  return {
    ...state,
    popOutState: {
      ...state.popOutState,
      visibilityState,
    },
  };
};

const setActiveVideoPlayState = (
  state: VideoState,
  playerId: PlayerID | null | undefined,
  playState: PlayState,
): VideoState => {
  if (state.activeVideoState.id !== playerId) {
    return state;
  }
  return {
    ...state,
    activeVideoState: {
      ...state.activeVideoState,
      playState,
    },
  };
};

const setActiveVideo = (
  state: VideoState,
  id: PlayerID | null,
  playState: PlayState,
): VideoState => {
  return {
    ...state,
    activeVideoState: {
      ...state.activeVideoState,
      id,
      playState,
    },
  };
};

const setInlinePlayerVisibilityState = (
  state: VideoState,
  playerId: PlayerID,
  sourceContainerVisibilityState: VisibilityState,
): VideoState => {
  return {
    ...state,
    [playerId]: {
      ...state[playerId],
      sourceContainerVisibilityState,
    },
  };
};

export const videosStateReducer = (
  state: VideoState,
  action: VideoAction,
): VideoState => {
  switch (action.type) {
    case 'UPDATE_PLAYER_SOURCE_CONTAINER':
      return updatePlayerSourceContainer(
        state,
        action.payload.playerId,
        action.payload.sourceContainer,
      );
    case 'ADD_VIDEO_PLAYER':
      return addVideoPlayerAndSourceContainer(
        state,
        action.payload.playerId,
        action.payload.player,
        action.payload.sourceContainer,
      );
    case 'REMOVE_VIDEO_PLAYER':
      return removeVideoPlayerData(state, action.payload);
    case 'POP_VIDEO':
      return setVideoAsPopped(state, action.payload);
    case 'UN_POP_VIDEO':
      return removePoppedStateOfVideo(state, action.payload);
    case 'SET_CONTROLLER_INSTANCE':
      return setVideoJSControllerInstance(
        state,
        action.payload.playerId,
        action.payload.controller,
      );
    case 'SET_POP_OUT_VISIBILITY_STATE':
      return setPopOutVisibility(state, action.payload);
    case 'SET_ACTIVE_VIDEO_PLAY_STATE':
      return setActiveVideoPlayState(
        state,
        action.payload.id,
        action.payload.playState,
      );
    case 'SET_ACTIVE_VIDEO_STATE':
      return setActiveVideo(state, action.payload.id, action.payload.playState);
    case 'SET_INLINE_PLAYER_VISIBILITY_STATE':
      return setInlinePlayerVisibilityState(
        state,
        action.payload.id,
        action.payload.state,
      );
  }

  return state;
};
